import { type ReactNode, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'motion/react'

// Функция для управления вертикальными свайпами Telegram
// Вынесена из компонента чтобы не зависеть от контекста
function getTelegramWebApp() {
  if (typeof window !== 'undefined' && (window as any).Telegram?.WebApp) {
    return (window as any).Telegram.WebApp
  }
  return null
}

function disableTelegramSwipes() {
  const webApp = getTelegramWebApp()
  webApp?.disableVerticalSwipes?.()
}

function enableTelegramSwipes() {
  const webApp = getTelegramWebApp()
  webApp?.enableVerticalSwipes?.()
}

export interface BottomSheetProps {
  /**
   * Открыто ли bottom sheet
   */
  isOpen: boolean
  /**
   * Обработчик закрытия
   */
  onClose: () => void
  /**
   * Заголовок bottom sheet
   */
  title?: string
  /**
   * Содержимое bottom sheet
   */
  children: ReactNode
  /**
   * Высота bottom sheet (в процентах от высоты экрана)
   * @default 50
   */
  height?: number
  /**
   * Snap points (точки остановки в процентах от высоты экрана)
   * Используются для определения позиций, к которым будет "прилипать" bottom sheet при свайпе
   * @default [25, 50, 90]
   */
  snapPoints?: number[]
  /**
   * Показывать handle (ручку для swipe) в верхней части bottom sheet
   * @default true
   */
  showHandle?: boolean
  /**
   * Показывать кнопку закрытия в заголовке
   * @default false
   */
  showCloseButton?: boolean
  /**
   * Закрывать при клике на overlay (фон)
   * @default true
   */
  closeOnOverlayClick?: boolean
  /**
   * Блокировать прокрутку фона когда bottom sheet открыт
   * @default true
   */
  preventBodyScroll?: boolean
  /**
   * Callback для haptic feedback при взаимодействии
   * Вызывается при закрытии или переходе между snap points
   */
  onHapticFeedback?: (type?: 'light' | 'selection') => void
  /**
   * Подогнать высоту под контент (без скролла внутри)
   * @default false
   */
  fitContent?: boolean
}

/**
 * UI компонент: Bottom Sheet
 * 
 * Компонент для отображения выдвижных панелей снизу экрана
 * с поддержкой swipe жестов и snap points.
 * 
 * @example
 * ```tsx
 * <BottomSheet
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   title="Выберите вариант"
 *   height={50}
 *   snapPoints={[25, 50, 90]}
 * >
 *   <div>Контент</div>
 * </BottomSheet>
 * ```
 */
export function BottomSheet({
  isOpen,
  onClose,
  title,
  children,
  height = 50,
  snapPoints = [25, 50, 90],
  showHandle = true,
  showCloseButton = false,
  closeOnOverlayClick = true,
  preventBodyScroll = true,
  onHapticFeedback,
  fitContent = false,
}: BottomSheetProps) {
  const sheetRef = useRef<HTMLDivElement>(null)
  const [dragY, setDragY] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const startYRef = useRef<number>(0)
  const startTimeRef = useRef<number>(0)
  
  // Note: snapPoints is reserved for future use
  void snapPoints

  // Сброс позиции при открытии
  useEffect(() => {
    if (isOpen) {
      setDragY(0)
      setIsDragging(false)
    }
    // При закрытии НЕ сбрасываем dragY сразу - позволим exit анимации использовать текущую позицию
  }, [isOpen])

  // Управление вертикальными свайпами Telegram Mini App
  // Отключаем нативный свайп закрытия когда BottomSheet открыт
  useEffect(() => {
    if (isOpen) {
      disableTelegramSwipes()
    } else {
      enableTelegramSwipes()
    }
    
    // Cleanup: включаем свайпы обратно при размонтировании
    return () => {
      enableTelegramSwipes()
    }
  }, [isOpen])

  // Блокировка прокрутки фона
  useEffect(() => {
    if (!preventBodyScroll) return

    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }

    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen, preventBodyScroll])

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Проверяем, что клик был именно на overlay, а не на дочерних элементах
    if (closeOnOverlayClick && !isDragging && e.target === e.currentTarget) {
      e.preventDefault()
      e.stopPropagation()
      onHapticFeedback?.('light')
      onClose()
    }
  }

  const handleOverlayMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    // Обрабатываем mousedown для более надежного определения клика на overlay
    if (closeOnOverlayClick && !isDragging && e.target === e.currentTarget) {
      const overlay = e.currentTarget
      const handleMouseUp = (upEvent: MouseEvent) => {
        if (upEvent.target === overlay) {
          onHapticFeedback?.('light')
          onClose()
        }
        document.removeEventListener('mouseup', handleMouseUp)
      }
      document.addEventListener('mouseup', handleMouseUp)
    }
  }

  const overlayTouchStartRef = useRef<number | null>(null)

  const handleOverlayTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    // Обрабатываем touchstart для закрытия по tap на overlay
    // Проверяем, что не начался drag на bottom sheet
    if (closeOnOverlayClick && !isDragging && e.target === e.currentTarget) {
      const target = e.target as HTMLElement
      // Проверяем, что клик не на bottom sheet
      if (!target.closest('[data-bottom-sheet]')) {
        overlayTouchStartRef.current = e.touches[0].clientY
      }
    }
  }

  const handleOverlayTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
    // Закрываем если был tap на overlay (без движения)
    if (closeOnOverlayClick && !isDragging && e.target === e.currentTarget && overlayTouchStartRef.current !== null) {
      const deltaY = Math.abs(e.changedTouches[0].clientY - overlayTouchStartRef.current)
      // Если движение было меньше 10px, считаем это tap
      if (deltaY < 10) {
        onHapticFeedback?.('light')
        onClose()
      }
      overlayTouchStartRef.current = null
    }
  }

  // Универсальный обработчик начала drag (touch + pointer для Telegram Web)
  const handleDragStart = (clientY: number, target: HTMLElement) => {
    const dragArea = target.closest('[data-drag-area]')
    if (dragArea) {
      startYRef.current = clientY
      startTimeRef.current = Date.now()
      setIsDragging(true)
      setDragY(0)
    }
  }

  const handleDragMove = (clientY: number) => {
    if (!isDragging) return
    const deltaY = clientY - startYRef.current
    // Разрешаем только движение вниз (положительное значение)
    if (deltaY > 0) {
      setDragY(deltaY)
    }
  }

  const handleDragEnd = () => {
    if (!isDragging) return

    setIsDragging(false)

    if (typeof window === 'undefined') {
      setDragY(0)
      return
    }

    const windowHeight = window.innerHeight
    const dragPercent = (dragY / windowHeight) * 100
    const dragDuration = Date.now() - startTimeRef.current
    const dragVelocity = dragY / dragDuration

    // Закрываем если:
    // 1. Перетащили больше 25% высоты экрана
    // 2. Или перетащили больше 100px с достаточной скоростью
    if (dragPercent > 25 || (dragY > 100 && dragVelocity > 0.5)) {
      onHapticFeedback?.('light')
      onClose()
      setTimeout(() => {
        setDragY(0)
      }, 350)
    } else {
      // Возвращаем на место
      setDragY(0)
    }
  }

  const handleTouchStart = (e: React.TouchEvent) => {
    const target = e.target as HTMLElement
    const dragArea = target.closest('[data-drag-area]')
    if (dragArea) {
      e.preventDefault()
      handleDragStart(e.touches[0].clientY, target)
    }
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return
    e.preventDefault()
    handleDragMove(e.touches[0].clientY)
  }

  const handleTouchEnd = () => {
    handleDragEnd()
  }

  // Pointer events для Telegram Web (mouse/touch унифицированные)
  const handleSheetPointerDown = (e: React.PointerEvent) => {
    const target = e.target as HTMLElement
    const dragArea = target.closest('[data-drag-area]')
    if (dragArea) {
      e.preventDefault()
      ;(e.target as HTMLElement).setPointerCapture(e.pointerId)
      handleDragStart(e.clientY, target)
    }
  }

  const handleSheetPointerMove = (e: React.PointerEvent) => {
    if (!isDragging) return
    e.preventDefault()
    handleDragMove(e.clientY)
  }

  const handleSheetPointerUp = (e: React.PointerEvent) => {
    if (isDragging) {
      ;(e.target as HTMLElement).releasePointerCapture?.(e.pointerId)
      handleDragEnd()
    }
  }

  // Вычисляем высоту в пикселях
  const getHeightInPx = () => {
    if (typeof window === 'undefined') return '50vh'
    return `${(window.innerHeight * height) / 100}px`
  }

  // Обработчик клика для Telegram Web (pointer events)
  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (closeOnOverlayClick && !isDragging && e.target === e.currentTarget) {
      e.preventDefault()
    }
  }

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (closeOnOverlayClick && !isDragging && e.target === e.currentTarget) {
      e.preventDefault()
      e.stopPropagation()
      onHapticFeedback?.('light')
      onClose()
    }
  }

  // Рендерим в portal чтобы избежать проблем с overflow: hidden и transform у родителей
  if (typeof document === 'undefined') return null
  
  return createPortal(
    <AnimatePresence mode="wait">
      {isOpen && (
        <div
          key="bottom-sheet-container"
          className="fixed z-[100]"
          style={{ 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0,
            // Для Telegram Web App
            position: 'fixed',
          }}
          onClick={handleOverlayClick}
          onMouseDown={handleOverlayMouseDown}
          onPointerDown={handlePointerDown}
          onPointerUp={handlePointerUp}
          role="dialog"
          aria-modal="true"
          aria-labelledby={title ? 'bottom-sheet-title' : undefined}
        >
          {/* Overlay - покрывает весь экран */}
          <motion.div
            key="overlay"
            initial={{ opacity: 0 }}
            animate={{ 
              opacity: isDragging ? Math.max(0.3, 1 - dragY / (typeof window !== 'undefined' ? window.innerHeight : 1000)) : 1,
            }}
            exit={{ opacity: 0 }}
            transition={isDragging ? { duration: 0 } : { duration: 0.2 }}
            className="bg-black/60 backdrop-blur-sm"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              // Гарантируем полное покрытие (dvh для мобильных браузеров)
              minHeight: '100dvh',
            }}
            aria-hidden="true"
            onTouchStart={handleOverlayTouchStart}
            onTouchEnd={handleOverlayTouchEnd}
            onPointerUp={handlePointerUp}
          />

          {/* Bottom Sheet */}
          <motion.div
            key="bottom-sheet"
            ref={sheetRef}
            data-bottom-sheet
            initial={{ y: '100%' }}
            animate={{ 
              y: dragY,
            }}
            exit={{ 
              y: '100%',
              transition: { type: 'spring', stiffness: 300, damping: 30 }
            }}
            transition={isDragging ? { duration: 0 } : { type: 'spring', stiffness: 300, damping: 30 }}
            className="absolute bottom-0 left-0 right-0 bg-white dark:bg-[#0F1113] rounded-t-[32px] shadow-xl flex flex-col lg:left-[240px] lg:max-w-[calc(100%-240px)]"
            style={{ 
              maxHeight: '90vh',
              height: fitContent ? 'auto' : getHeightInPx(),
            }}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onPointerDown={handleSheetPointerDown}
            onPointerMove={handleSheetPointerMove}
            onPointerUp={handleSheetPointerUp}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Handle */}
            {showHandle && (
              <div 
                data-drag-area
                className="flex justify-center pt-3 pb-2 flex-shrink-0 cursor-grab active:cursor-grabbing touch-none select-none"
              >
                <div className="w-12 h-1.5 bg-gray-300 dark:bg-gray-600 rounded-full" />
              </div>
            )}

            {/* Header */}
            {title && (
              <div 
                data-drag-area
                className="flex items-center justify-between px-6 py-4 border-b border-silk dark:border-dark-border flex-shrink-0 cursor-grab active:cursor-grabbing touch-none select-none"
              >
                <h2
                  id="bottom-sheet-title"
                  className="text-xl font-semibold text-ink dark:text-white"
                >
                  {title}
                </h2>
                {showCloseButton && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      onHapticFeedback?.('light')
                      onClose()
                    }}
                    className="ml-auto -mr-2 p-2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors touch-target rounded-lg hover:bg-gray-100 dark:hover:bg-dark-elevated"
                    aria-label="Закрыть"
                  >
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            )}

            {/* Content */}
            <div
              className={fitContent ? "" : "flex-1 overflow-y-auto overscroll-contain min-h-0"}
              style={fitContent ? undefined : { WebkitOverflowScrolling: 'touch' }}
              onTouchStart={(e) => {
                // Разрешаем прокрутку контента, если не начался drag
                if (!isDragging) {
                  e.stopPropagation()
                }
              }}
            >
              {children}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  )
}
